import { useEffect, type PropsWithChildren } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Capacitor } from "@capacitor/core";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Login from "./pages/Login";
import ReservasPage from "./pages/Reservas";
import AusenciasPage from "./pages/Ausencias";
import HorarioPage from "./pages/Horario";
import TareasPage from "./pages/Tareas";
import ClientesPage from "./pages/Clientes";
import AccessDeniedPage from "./pages/AccessDenied";
import BackHandler from "./components/BackHandler";
import ScrollToTop from "./components/ScrollToTop";
import { useProfessionalAuth } from "./auth/ProfessionalAuthContext";
import { NO_ACCESS_ROUTE, PUBLIC_PATHS, canAccessPath } from "./auth/permissions";
import "./App.css";
import "./styles/shell.css";

function LoadingScreen() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(180deg, #111827 0%, #0f172a 100%)",
        color: "#cbd5e1",
        fontWeight: 700
      }}
    >
      Cargando sesión...
    </div>
  );
}

function RequireCoachAuth({ children }: PropsWithChildren) {
  const { ready, isAuthenticated } = useProfessionalAuth();

  if (!ready) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PermissionGuard({ children }: PropsWithChildren) {
  const location = useLocation();
  const { permissions } = useProfessionalAuth();

  if (!canAccessPath(location.pathname, permissions)) {
    return <Navigate to={NO_ACCESS_ROUTE} replace />;
  }

  return <>{children}</>;
}

function LoginRoute() {
  const { ready, isAuthenticated, defaultRoute } = useProfessionalAuth();

  if (!ready) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to={defaultRoute} replace />;
  }

  return <Login />;
}

function RootRedirect() {
  const { ready, isAuthenticated, defaultRoute } = useProfessionalAuth();

  if (!ready) {
    return <LoadingScreen />;
  }

  return <Navigate to={isAuthenticated ? defaultRoute : "/login"} replace />;
}

function CatchAllRedirect() {
  const { ready, isAuthenticated, defaultRoute } = useProfessionalAuth();

  if (!ready) {
    return <LoadingScreen />;
  }

  return <Navigate to={isAuthenticated ? defaultRoute : "/login"} replace />;
}

export default function App() {
  const { pathname } = useLocation();
  const { ready } = useProfessionalAuth();
  const hideChrome = pathname === "/login";

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    void StatusBar.setOverlaysWebView({ overlay: true });
    void StatusBar.setStyle({ style: Style.Dark });
  }, []);

  useEffect(() => {
    const FOOTER_BASE = 86;

    const setViewportVars = () => {
      const viewport = window.visualViewport;
      const height = (viewport?.height ?? window.innerHeight) * 0.01;
      document.documentElement.style.setProperty("--vh", `${height}px`);

      if (viewport) {
        const top = viewport.offsetTop ?? 0;
        const bottom = Math.max(window.innerHeight - (viewport.height + viewport.offsetTop), 0);
        document.documentElement.style.setProperty("--safe-top", `${top}px`);
        document.documentElement.style.setProperty("--safe-bottom", `${bottom}px`);
        document.documentElement.style.setProperty("--footer-height", `${FOOTER_BASE + bottom}px`);
      }
    };

    setViewportVars();

    window.visualViewport?.addEventListener("resize", setViewportVars);
    window.visualViewport?.addEventListener("scroll", setViewportVars);
    window.addEventListener("resize", setViewportVars);

    return () => {
      window.visualViewport?.removeEventListener("resize", setViewportVars);
      window.visualViewport?.removeEventListener("scroll", setViewportVars);
      window.removeEventListener("resize", setViewportVars);
    };
  }, []);

  if (!ready && !PUBLIC_PATHS.has(pathname)) {
    return <LoadingScreen />;
  }

  return (
    <>
      <BackHandler />
      <ScrollToTop />

      <div className={hideChrome ? undefined : "shell-app"}>
        {!hideChrome ? <Header /> : null}

        <div className={hideChrome ? undefined : "shell-main"}>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginRoute />} />
            <Route
              path={NO_ACCESS_ROUTE}
              element={
                <RequireCoachAuth>
                  <AccessDeniedPage />
                </RequireCoachAuth>
              }
            />

            <Route
              path="/reservas"
              element={
                <RequireCoachAuth>
                  <PermissionGuard>
                    <ReservasPage />
                  </PermissionGuard>
                </RequireCoachAuth>
              }
            />
            <Route
              path="/ausencias"
              element={
                <RequireCoachAuth>
                  <PermissionGuard>
                    <AusenciasPage />
                  </PermissionGuard>
                </RequireCoachAuth>
              }
            />
            <Route
              path="/horario"
              element={
                <RequireCoachAuth>
                  <PermissionGuard>
                    <HorarioPage />
                  </PermissionGuard>
                </RequireCoachAuth>
              }
            />
            <Route
              path="/tareas"
              element={
                <RequireCoachAuth>
                  <PermissionGuard>
                    <TareasPage />
                  </PermissionGuard>
                </RequireCoachAuth>
              }
            />
            <Route
              path="/clientes"
              element={
                <RequireCoachAuth>
                  <PermissionGuard>
                    <ClientesPage />
                  </PermissionGuard>
                </RequireCoachAuth>
              }
            />

            <Route path="*" element={<CatchAllRedirect />} />
          </Routes>
        </div>

        {!hideChrome ? <Footer /> : null}
      </div>
    </>
  );
}
